import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import React, { useEffect, useState, useRef } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { AmsaInputProps } from "../../interfaces/AmsaInputs";

const AmsaEditor: React.FC<AmsaInputProps> = ({
  data,
  setValue,
}: AmsaInputProps) => {
  // Initialize editor state from data.value if it exists
  const [editorState, setEditorState] = useState(() => {
    if (data.value) {
      try {
        const valueToParse = typeof data.value === 'string' ? data.value : JSON.stringify(data.value);
        const contentState = convertFromRaw(JSON.parse(valueToParse));
        return EditorState.createWithContent(contentState);
      } catch (error) {
        return EditorState.createEmpty();
      }
    }
    return EditorState.createEmpty();
  });

  // Track if we're updating from user input to prevent race conditions
  const isUserInputRef = useRef(false);
  const lastSetValueRef = useRef<string | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Skip if this is the initial mount or if the update came from user input
    if (!isMountedRef.current || isUserInputRef.current) {
      isUserInputRef.current = false;
      return;
    }

    // Only update if the value is different from what we last set
    if (data.value === lastSetValueRef.current) {
      return;
    }

    // Value changed externally, update editor state
    if (data.value) {
      try {
        const valueToParse = typeof data.value === 'string' ? data.value : JSON.stringify(data.value);
        const contentState = convertFromRaw(JSON.parse(valueToParse));
        setEditorState(EditorState.createWithContent(contentState));
        lastSetValueRef.current = null; // Reset since this is external
      } catch (error) {
        console.warn('Failed to parse AmsaEditor value:', error);
      }
    } else if (!data.value && editorState.getCurrentContent().hasText()) {
      // External reset to empty
      setEditorState(EditorState.createEmpty());
      lastSetValueRef.current = null;
    }
  }, [data.value]);

  const onEditorStateChange = (newEditorState: EditorState) => {
    // Mark this as user input
    isUserInputRef.current = true;
    setEditorState(newEditorState);
    
    const contentState = newEditorState.getCurrentContent();
    const contentStateString = JSON.stringify(convertToRaw(contentState));
    
    // Track what we're setting
    lastSetValueRef.current = contentStateString;
    setValue(contentStateString);
  };

  return (
    <div className="block h-full">
      <span className="text-sm font-medium text-gray-700">{data.label}</span>
      <Editor
        editorState={editorState}
        toolbarClassName="toolbarClassName"
        wrapperClassName="h-64 overflow-hidden  border rounded-md"
        onEditorStateChange={onEditorStateChange}
        toolbar={{
          options: ["inline", "list", "blockType"],
          inline: {
            options: ["bold", "italic", "underline"],
          },
        }}
      />
    </div>
  );
};

export default AmsaEditor;
